// src/modules/cad/cad-import.js

import {
    getCADLayers,
    readCADLayer,
    analyzeCADFeatures
} from "./cad-reader.js";

import {
    initCADDatabase,
    isCADDatabaseReady
} from "./cad-db.js";

import {
    showCADLayerPicker
} from "./cad-dialog.js";

import CADRenderer, {
    findNearestCADVertex,
    showCADVertexPopup
} from "./cad-renderer.js";

import AppState from "../../core/app.js";
import LayerManager from "../layers.js";
import UI from "../ui.js";


/**
 * ============================================================
 * CAD Import
 * ============================================================
 */

export const CADImport = {

    /**
     * ========================================================
     * Open CAD File
     * ========================================================
     *
     * Workflow:
     *
     * DXF / DWG
     *     ↓
     * DuckDB
     *     ↓
     * Read metadata
     *     ↓
     * Layer + CRS selection
     *     ↓
     * Read selected layers
     *     ↓
     * CRS transformation
     *     ↓
     * Analyze geometry
     *     ↓
     * Render Leaflet
     *     ↓
     * LayerManager
     *
     * ========================================================
     */

    async open(file) {

        try {
            await initCADDatabase();

            if (!isCADDatabaseReady()) {
                throw new Error(
                    "DuckDB belum siap."
                );
            }
            console.log("================================");
            console.log("[CAD] OPEN");
            console.log("[CAD] File:", file.name);
            console.log("================================");


            // ==================================================
            // STEP 1
            // Read CAD metadata
            // ==================================================

            console.log(
                "[CAD] STEP 1: getCADLayers()"
            );

            console.log(
                "[CAD] DuckDB READY. Processing CAD..."
            );


            const result =
                await getCADLayers(file);


            console.log(
                "[CAD] getCADLayers() SUCCESS:",
                result
            );


            // ==================================================
            // Check layers
            // ==================================================

            if (
                !result.layers ||
                result.layers.length === 0
            ) {

                alert(
                    "Tidak ada drawing layer yang ditemukan pada file CAD."
                );

                return null;
            }


            // ==================================================
            // STEP 2
            // Layer + CRS picker
            // ==================================================

            console.log(
                "[CAD] STEP 2: Opening layer + CRS picker..."
            );


            const selection =
                await showCADLayerPicker({

                    fileName:
                        file.name,

                    layers:
                        result.layers

                });


            // ==================================================
            // Cancel
            // ==================================================

            if (!selection) {

                console.log(
                    "[CAD] Import cancelled."
                );

                return null;
            }


            // ==================================================
            // Selected layers
            // ==================================================

            const selectedLayers =
                selection.layers;


            // ==================================================
            // Source CRS
            // ==================================================

            const sourceCRS =
                selection.sourceCRS;


            console.log(
                "[CAD] Source CRS:",
                sourceCRS
            );


            console.log(
                "[CAD] Selected layers:"
            );


            console.table(

                selectedLayers.map(
                    (layer, index) => ({

                        index,

                        name:
                            layer.name ??
                            layer.layer_name

                    })
                )

            );


            // ==================================================
            // STEP 3
            // Read selected CAD layers
            // ==================================================

            console.log(
                "[CAD] STEP 3: Reading selected layers..."
            );


            const layerResults = [];


            for (
                const layer
                of selectedLayers
            ) {

                const layerName =
                    layer.name ??
                    layer.layer_name;


                if (!layerName) {

                    console.warn(
                        "[CAD] Layer name tidak ditemukan:",
                        layer
                    );

                    continue;
                }


                console.log(
                    "--------------------------------"
                );


                console.log(
                    "[CAD] Reading layer:",
                    layerName
                );


                // ----------------------------------------------
                // Read CAD layer
                // ----------------------------------------------

                const data =
                    await readCADLayer(

                        result.virtualPath,

                        layerName,

                        sourceCRS

                    );


                // ----------------------------------------------
                // Analyze geometry
                // ----------------------------------------------

                const analysis =
                    analyzeCADFeatures(
                        data.rows
                    );


                data.analysis =
                    analysis;


                // ----------------------------------------------
                // Store
                // ----------------------------------------------

                layerResults.push(
                    data
                );

            }


            // ==================================================
            // Check result
            // ==================================================

            if (
                layerResults.length === 0
            ) {

                alert(
                    "Tidak ada CAD layer yang berhasil dibaca."
                );

                return null;
            }


            // ==================================================
            // STEP 4
            // Render CAD
            // ==================================================

            console.log(
                "================================"
            );


            console.log(
                "[CAD] STEP 4: Rendering CAD..."
            );


            const cadLayer =
                CADRenderer.render(
                    layerResults
                );
            
            console.log(
                "[CAD DEBUG] CAD vertex index:",
                cadLayer?._cadVertexIndex
            );

            console.log(
                "[CAD DEBUG] CAD vertex count:",
                cadLayer?._cadVertexIndex?.length
            );


            if (!cadLayer) {

                throw new Error(
                    "Tidak ada geometry CAD yang dapat dirender."
                );
            }


            console.log(
                "[CAD] CAD renderer created."
            );


            // ==================================================
            // STEP 5
            // Add to Leaflet map
            // ==================================================

            console.log(
                "[CAD] STEP 5: Adding CAD to map..."
            );


            cadLayer.addTo(
                AppState.map
            );


            console.log(
                "[CAD] CAD layer added to map."
            );

            // ==================================================
            // CAD Vertex Elevation Labels
            // ==================================================

            if (
                cadLayer._cadElevationLabels
            ) {


                // ==================================================
                // Vertex Elevation Labels — tampil mulai Zoom 16
                // ==================================================

                const elevationLabels =
                    cadLayer._cadElevationLabels;
                
                const updateElevationLabels = () => {

                    const zoom =
                        AppState.map.getZoom();

                    // ==================================================
                    // TENTUKAN MINIMUM DISTANCE
                    // ==================================================

                    let minDistance = null;

                    if (zoom < 18) {

                        minDistance = null;

                    } else if (zoom < 19) {

                        minDistance = 150;

                    } else if (zoom < 20) {

                        minDistance = 50;

                    } else {

                        minDistance = 20;
                    }

                    const labels =
                        elevationLabels.getLayers();

                    // ==================================================
                    // ZOOM < 18
                    // ==================================================

                    if (minDistance === null) {

                        if (
                            AppState.map.hasLayer(
                                elevationLabels
                            )
                        ) {

                            AppState.map.removeLayer(
                                elevationLabels
                            );
                        }

                        for (
                            const marker
                            of labels
                        ) {

                            const callout =
                                marker._cadCallout;

                            if (
                                callout &&
                                AppState.map.hasLayer(
                                    callout
                                )
                            ) {

                                AppState.map.removeLayer(
                                    callout
                                );
                            }
                        }

                        console.log(
                            "[CAD] Vertex labels hidden:",
                            "zoom =", zoom
                        );

                        return;
                    }

                    // ==================================================
                    // ZOOM >= 18
                    // ==================================================

                    if (
                        !AppState.map.hasLayer(
                            elevationLabels
                        )
                    ) {

                        elevationLabels.addTo(
                            AppState.map
                        );
                    }

                    // ==================================================
                    // FILTER BERDASARKAN JARAK
                    // ==================================================

                    const selectedPositions = [];

                    let shown = 0;

                    for (
                        const marker
                        of labels
                    ) {

                        const position =
                            marker._cadLatLng;

                        if (!position) {
                            continue;
                        }

                        let tooClose = false;

                        for (
                            const selectedPosition
                            of selectedPositions
                        ) {

                            if (
                                position.distanceTo(
                                    selectedPosition
                                ) < minDistance
                            ) {

                                tooClose = true;

                                break;
                            }
                        }

                        const callout =
                            marker._cadCallout;

                        // ==================================================
                        // TAMPILKAN
                        // ==================================================

                        if (!tooClose) {

                            selectedPositions.push(
                                position
                            );

                            if (
                                !AppState.map.hasLayer(marker)
                            ) {

                                marker.addTo(
                                    AppState.map
                                );
                            }

                            if (
                                callout &&
                                !AppState.map.hasLayer(
                                    callout
                                )
                            ) {

                                callout.addTo(
                                    AppState.map
                                );
                            }

                            shown++;

                        }

                        // ==================================================
                        // SEMBUNYIKAN
                        // ==================================================

                        else {

                            if (
                                AppState.map.hasLayer(marker)
                            ) {

                                AppState.map.removeLayer(
                                    marker
                                );
                            }


                            if (
                                callout &&
                                AppState.map.hasLayer(
                                    callout
                                )
                            ) {

                                AppState.map.removeLayer(
                                    callout
                                );
                            }
                        }
                    }


                };

  

                AppState.map.on(
                    "zoomend",
                    updateElevationLabels
                );

                updateElevationLabels();

                console.log(
                    "[CAD] Vertex elevation labels added:",
                    cadLayer._cadElevationLabels
                        .getLayers()
                        .length
                );

            } else {

                console.warn(
                    "[CAD] _cadElevationLabels tidak ditemukan."
                );
            }

            /**
             * ============================================================
             * CAD Vertex Identify
             * ============================================================
             */

            if (
                !AppState.map._cadVertexIdentifyHandler
            ) {

                AppState.map._cadVertexIdentifyHandler =
                    function (event) {

                        const cadLayers =
                            AppState.map._cadLayers;

                        if (
                            !Array.isArray(cadLayers) ||
                            cadLayers.length === 0
                        ) {
                            return;
                        }

                        const radiusMeters = 3;

                        let nearest = null;

                        for (
                            const layer of cadLayers
                        ) {

                            const result =
                                findNearestCADVertex(
                                    layer,
                                    event.latlng,
                                    radiusMeters
                                );

                            if (
                                result &&
                                (
                                    !nearest ||
                                    result.distance <
                                    nearest.distance
                                )
                            ) {

                                nearest =
                                    result;

                            }
                        }

                        if (!nearest) {
                            return;
                        }

                        console.log(
                            "[CAD] Vertex identified:",
                            {
                                handle:
                                    nearest.handle,

                                elevation:
                                    nearest.z,

                                distance:
                                    nearest.distance
                            }
                        );

                        showCADVertexPopup(
                            AppState.map,
                            nearest
                        );
                    };

                AppState.map.on(
                    "click",
                    AppState.map._cadVertexIdentifyHandler
                );
            }

            if (
                !Array.isArray(
                    AppState.map._cadLayers
                )
            ) {
                AppState.map._cadLayers = [];
            }

            AppState.map._cadLayers.push(
                cadLayer
            );
                        // ==================================================
            // STEP 6
            // Get bounds
            // ==================================================

            const bounds =
                cadLayer.getBounds();


            console.log(
                "[CAD] CAD bounds:",
                bounds
            );


            // ==================================================
            // STEP 7
            // Zoom to CAD
            // ==================================================

            if (
                bounds &&
                bounds.isValid()
            ) {

                console.log(
                    "[CAD] Zooming to CAD bounds..."
                );


                AppState.map.fitBounds(

                    bounds,

                    {

                        padding: [
                            30,
                            30
                        ]

                    }

                );

            } else {

                console.warn(
                    "[CAD] CAD bounds tidak valid."
                );

            }


            // ==================================================
            // STEP 8
            // Layer Manager
            // ==================================================

            console.log(
                "[CAD] STEP 8: Adding to LayerManager..."
            );


            const layerName =
                file.name.replace(
                    /\.[^/.]+$/,
                    ""
                );


            const managedLayer =
                LayerManager.add(

                    layerName,

                    "cad",

                    cadLayer,

                    bounds

                );


            console.log(
                "[CAD] LayerManager:",
                managedLayer
            );


            // ==================================================
            // STEP 9
            // Refresh Layer Panel
            // ==================================================

            console.log(
                "[CAD] STEP 9: Refreshing layer UI..."
            );


            UI.renderLayerList();


            // ==================================================
            // Final result
            // ==================================================

            const importResult = {

                file,

                virtualPath:
                    result.virtualPath,

                layers:
                    selectedLayers,

                layerResults,

                sourceCRS,

                outputCRS:
                    "OGC:CRS84",

                metadata:
                    result.metadata,

                leafletLayer:
                    cadLayer,

                bounds,

                managedLayer

            };


            console.log(
                "================================"
            );


            console.log(
                "[CAD] READ COMPLETE"
            );


            console.log(
                "[CAD] IMPORT RESULT:",
                importResult
            );


            console.log(
                "================================"
            );


            return importResult;


        } catch (error) {

            // ==================================================
            // Error handling
            // ==================================================

            console.error(
                "================================"
            );


            console.error(
                "[CAD] IMPORT ERROR:",
                error
            );


            console.error(
                "================================"
            );


            alert(
                `Gagal membuka file CAD.\n\n${error.message}`
            );


            return null;

        }

    }

};


export default CADImport;